/**
 * Merges structure + progress into the legacy all-subjects RootSubject[] and
 * derives selectedSubject from the URL slug. Used by SubjectContainer,
 * AllSubjectsContainer, and AchievementsContainer. Structure and progress are
 * fetched by StudentDataContext and passed in.
 */

import { useState, useMemo, useEffect } from "react";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import type { RootSubject } from "@/ts/interfaces/subjects/api/subjectsApiInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";
import type { GradeStructureApiResponse } from "@ts/interfaces/structure/api/gradeStructureApiInterface";
import type { GradeProgressApiResponse } from "@ts/interfaces/progress/api/gradeProgressApiInterface";
import { mergeSubjects } from "./mergeSubjects";

interface UseSubjectsDataParams {
  urlSubjectSlug: string | undefined;
  structure: GradeStructureApiResponse | null;
  progress: GradeProgressApiResponse | null;
  structureStatus: REQUEST_STATUS;
  structureError: ErrorInterface | null;
  forGradeCode: number | undefined;
  isReady: boolean;
}

interface SubjectsDataState {
  subjects: RootSubject[];
  selectedSubject: RootSubject | null;
  forGradeCode: number | undefined;
  subjectsStatus: REQUEST_STATUS;
  subjectsError: ErrorInterface | null;
  isReady: boolean;
}

interface SubjectsDataActions {
  setSelectedSubject: (subject: RootSubject | null) => void;
}

export type SubjectsData = SubjectsDataState & SubjectsDataActions;

export function useSubjectsData({
  urlSubjectSlug,
  structure,
  progress,
  structureStatus,
  structureError,
  forGradeCode,
  isReady
}: UseSubjectsDataParams): SubjectsData {
  const [manualSubject, setManualSubject] = useState<RootSubject | null>(null);

  const subjects = useMemo(
    () => mergeSubjects(structure, progress),
    [structure, progress]
  );

  useEffect(() => {
    setManualSubject(null);
  }, [urlSubjectSlug]);

  // Return current data while a background refetch runs; nulling the selection
  // mid-refetch blanks the page. BLD2-523.
  const selectedSubject = useMemo(() => {
    if (manualSubject) return manualSubject;
    if (!urlSubjectSlug) return null;
    if (subjects.length === 0) return null;
    return subjects.find(s => s.slug === urlSubjectSlug) ?? null;
  }, [manualSubject, urlSubjectSlug, subjects]);

  return {
    subjects,
    selectedSubject,
    forGradeCode,
    subjectsStatus: structureStatus,
    subjectsError: structureError,
    isReady,
    setSelectedSubject: setManualSubject
  };
}
